import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Masonry from "react-masonry-css";
import Image from "next/image";
import Link from "next/link";
import SalifyFallback from "../salify-fallback/default";

export interface BlogPost {
  id: number;
  name: string;
  description: string;
  thumbnail: string;
  slug: string;
  date: string;
  author: string;
  category: string;
}

interface BlogWrapperProps {
  className?: string;
  itemsPerPage?: number;
  onLoadMore?: () => Promise<void>;
  hasMore?: boolean;
  title?: string;
}

const ITEMS_PER_LOAD = 12;

const breakpointColumns = {
  default: 4,
  1280: 4,
  1024: 3,
  768: 2,
  640: 1,
};

// Generate random height between min and max
const getRandomHeight = () => {
  const minHeight = 150;
  const maxHeight = 250;
  return Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight;
};

// Generate random width between min and max
const getRandomWidth = () => {
  const minWidth = 180;
  const maxWidth = 280;
  return Math.floor(Math.random() * (maxWidth - minWidth + 1)) + minWidth;
};

// Generate random background color with gradient
const getRandomBgColor = () => {
  const gradients = [
    "bg-gradient-to-br from-blue-50 to-indigo-50",
    "bg-gradient-to-br from-green-50 to-emerald-50",
    "bg-gradient-to-br from-yellow-50 to-amber-50",
    "bg-gradient-to-br from-pink-50 to-rose-50",
    "bg-gradient-to-br from-purple-50 to-violet-50",
    "bg-gradient-to-br from-red-50 to-orange-50",
    "bg-gradient-to-br from-cyan-50 to-blue-50",
    "bg-gradient-to-br from-lime-50 to-green-50",
    "bg-gradient-to-br from-orange-50 to-red-50",
    "bg-gradient-to-br from-violet-50 to-purple-50",
  ];
  return gradients[Math.floor(Math.random() * gradients.length)];
};

export const BlogWrapper: React.FC<BlogWrapperProps> = ({
  className = "",
  itemsPerPage = ITEMS_PER_LOAD,
  onLoadMore,
  hasMore = true,
  title,
}) => {
  const sectionTitle = (title || "Bài viết").trim() || "Bài viết";
  const blogs: BlogPost[] = SalifyFallback.useSelect("output:articles.data") ?? [];
  const [isLoading, setIsLoading] = useState(false);
  const [displayedPosts, setDisplayedPosts] = useState<BlogPost[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [postStyles, setPostStyles] = useState<{
    [key: string]: {
      height: number;
      width: number;
      bgColor: string;
    };
  }>({});

  const blogListKey = useMemo(
    () => (blogs.length ? blogs.map((p) => p.slug).join("|") : ""),
    [blogs],
  );

  const blogsRef = useRef(blogs);
  blogsRef.current = blogs;

  useEffect(() => {
    if (!blogListKey) return;
    const list = blogsRef.current;
    setDisplayedPosts(list.slice(0, ITEMS_PER_LOAD));
    setHasMoreItems(list.length > ITEMS_PER_LOAD);
  }, [blogListKey]);

  // Generate random styles for posts
  useEffect(() => {
    const styles: { [key: string]: { height: number; width: number; bgColor: string } } = {};
    displayedPosts.forEach((post) => {
      const key = `${post.id}-${post.slug}`;
      styles[key] = {
        height: getRandomHeight(),
        width: getRandomWidth(),
        bgColor: getRandomBgColor(),
      };
    });
    setPostStyles(styles);
  }, [displayedPosts]);

  const loadMorePosts = useCallback(async () => {
    if (isLoading || !hasMoreItems) return;

    setIsLoading(true);
    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * ITEMS_PER_LOAD;
      const endIndex = startIndex + ITEMS_PER_LOAD;

      const newPosts = blogs.slice(startIndex, endIndex);

      if (newPosts.length > 0) {
        setDisplayedPosts((prev) => [...prev, ...newPosts]);
        setCurrentPage(nextPage);
        // Check if we've reached the end of available posts
        setHasMoreItems(endIndex < blogs.length);
      } else {
        setHasMoreItems(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, isLoading, hasMoreItems]);

  const handleObserver = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && !isLoading && hasMoreItems) {
        await loadMorePosts();
      }
    },
    [isLoading, hasMoreItems, loadMorePosts],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    });

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [handleObserver]);

  return (
    <div className="w-full my-[50px]">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 md:w-[90%] lg:w-[60%]">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">{sectionTitle}</h1>
        <h2 className="mb-6 text-sm text-gray-600">
          Khám phá các bài viết mới nhất và nội dung nổi bật.
        </h2>
        <Masonry
          breakpointCols={breakpointColumns}
          className="flex w-auto -ml-[20px]"
          columnClassName="pl-[20px] bg-clip-padding"
        >
          {displayedPosts.map((post) => {
            const key = `${post.id}-${post.slug}`;
            const style = postStyles[key];

            return (
              <div key={key} className="mb-[20px]">
                <article
                  className={`rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 ${style?.bgColor || "bg-white"} group backdrop-blur-sm bg-opacity-80`}
                  style={{
                    minHeight: "250px",
                    height: "auto",
                  }}
                >
                  <Link
                    href={`/${post.slug}`}
                    className="relative z-10 block h-full touch-manipulation"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <Image
                        src={post.thumbnail}
                        alt={post.name}
                        fill
                        className="pointer-events-none object-cover transition-transform duration-300 group-hover:scale-110"
                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw"
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <div className="p-5">
                      <h2 className="text-sm font-semibold mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors duration-300">
                        {post.name}
                      </h2>
                      <p className="text-gray-600 text-xs leading-relaxed line-clamp-3 group-hover:text-gray-800 transition-colors duration-300">
                        {post.description}
                      </p>
                    </div>
                  </Link>
                </article>
              </div>
            );
          })}
        </Masonry>

        {/* Loading indicator and observer target */}
        {hasMoreItems && (
          <div ref={observerTarget} className="w-full h-20 flex items-center justify-center">
            {isLoading && (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce" />
                <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce delay-100" />
                <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce delay-200" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogWrapper;
